/**
 * The trainer's levels for "low sleep" and "low energy", for every surface that paints a reading.
 *
 * Both reads are shared cache entries: the kinds screen writes the level the server acknowledged
 * into the preferences, so a level moved there repaints every chart that reads this hook without a
 * request.
 *
 * ── Called beside the screen's own reads, once, and handed down ──────────────────────────────
 * `pending` belongs in the gate of whatever section paints a band: a line drawn under the default
 * level and repainted under the trainer's own when this lands is a partial answer in the shape of a
 * complete one. So the hook is called where the section's other reads start — never in a row, and
 * never in a child that mounts only once the data is in, which would open these two reads a round
 * trip late — and the levels travel down as a prop.
 *
 * A read that FAILED is not waited for: `model/wellbeingLevels` names what stands in its place.
 */

import { useQuery } from '@tanstack/react-query';

import { attentionPreferencesQueryOptions, vitalsCatalogQueryOptions } from '../api/clientQueries';
import type { WellbeingLevels } from '../model/wellbeingBands';
import { wellbeingLevels } from '../model/wellbeingLevels';

export interface WellbeingLevelsRead {
  readonly levels: WellbeingLevels;
  /** Either read is still on its first answer: the levels are not the trainer's yet. */
  readonly pending: boolean;
}

export function useWellbeingLevels(): WellbeingLevelsRead {
  const preferences = useQuery(attentionPreferencesQueryOptions());
  const catalog = useQuery(vitalsCatalogQueryOptions());
  return {
    levels: wellbeingLevels(preferences.data, catalog.data),
    pending: preferences.isPending || catalog.isPending,
  };
}
